import {
  MedicalQual,
  MiscQual,
  RopeQual,
  SearcherType,
  type D4HQualField,
  type D4HQualMappingEntry,
} from '@/types'

// Keyword rules for suggesting a FoS mapping from a D4H qualification title.
// First match wins, so more specific patterns come first (Type III before Type I).
// Suggestions are only ever shown for review. Nothing is mapped without the admin accepting it.
const RULES: { pattern: RegExp; field: D4HQualField; value: string }[] = [
  { pattern: /\b(MD|physician)\b/i, field: 'medical_qual', value: MedicalQual.MD },
  { pattern: /\b(paramedic|nurse|RN|EMT-?P)\b/i, field: 'medical_qual', value: MedicalQual.NurseParamedic },
  { pattern: /\b(A?EMT(-?B)?|emergency medical tech)/i, field: 'medical_qual', value: MedicalQual.EMT },
  { pattern: /\b(WFR|WEMR|EMR|PSFA|wilderness first responder)\b/i, field: 'medical_qual', value: MedicalQual.EMR },

  { pattern: /rope\s*tech/i, field: 'rope_qual', value: RopeQual.Technician },
  { pattern: /rope\s*op/i, field: 'rope_qual', value: RopeQual.Operator },
  { pattern: /rope\s*aware/i, field: 'rope_qual', value: RopeQual.Awareness },

  { pattern: /\b(type\s*(3|III)|SAR\s*Tech\s*(3|III))\b/i, field: 'searcher_type', value: SearcherType.Type3 },
  { pattern: /\b(type\s*(2|II)|SAR\s*Tech\s*(2|II))\b/i, field: 'searcher_type', value: SearcherType.Type2 },
  { pattern: /\b(type\s*(1|I)|SAR\s*Tech\s*(1|I))\b/i, field: 'searcher_type', value: SearcherType.Type1 },

  { pattern: /\bflanker\b/i, field: 'misc_quals', value: MiscQual.Flanker },
  { pattern: /\bK-?9\s*handler\b/i, field: 'misc_quals', value: MiscQual.K9 },
  { pattern: /swift\s*water|\bSRT\b|whitewater/i, field: 'misc_quals', value: MiscQual.SwiftWater },
  { pattern: /\b(UAV|UAS|sUAS|drone|part\s*107)\b/i, field: 'misc_quals', value: MiscQual.UAV },
  { pattern: /avalanche|\bavy\b|\bAIARE\b/i, field: 'misc_quals', value: MiscQual.Avalanche },
  { pattern: /\b(UTV|side[\s-]?by[\s-]?side)\b/i, field: 'misc_quals', value: MiscQual.UTV },
  { pattern: /e-?bike/i, field: 'misc_quals', value: MiscQual.EBike },
  { pattern: /man[\s-]?tracking|tracking|trailing/i, field: 'misc_quals', value: MiscQual.TrackingTrailing },
  { pattern: /\b(dive|diver|scuba)\b/i, field: 'misc_quals', value: MiscQual.Dive },
  { pattern: /large animal|\bATR\b/i, field: 'misc_quals', value: MiscQual.ATR },
  { pattern: /family liaison/i, field: 'misc_quals', value: MiscQual.FLO },
  { pattern: /\b(EVOC|driver)\b/i, field: 'misc_quals', value: MiscQual.Driver },
]

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function suggestQualMapping(title: string, customQuals: string[] = []): D4HQualMappingEntry | null {
  for (const rule of RULES) {
    if (rule.pattern.test(title)) return { field: rule.field, value: rule.value }
  }
  // Agency custom quals match on their exact name as a whole phrase
  for (const qual of customQuals) {
    if (new RegExp(`\\b${escapeRegExp(qual)}\\b`, 'i').test(title)) return { field: 'misc_quals', value: qual }
  }
  return null
}

// D4H teams usually prefix titles with a category: "Adv. Team Leader: IS242",
// "Adv. Team Leader - UTV". Split on the first ": " or " - " so titles can be grouped.
export function splitQualTitle(title: string): { group: string; name: string } {
  const match = title.match(/^(.+?)(?::\s+|\s+-\s+)(.+)$/)
  return match ? { group: match[1]!.trim(), name: match[2]!.trim() } : { group: '', name: title }
}
